import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Clock, Building2, IndianRupee, AlertTriangle, Users, Database, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const COLORS = ['#1D4ED8', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
const AGING_COLORS = { '0-7': '#10B981', '8-15': '#3B82F6', '16-30': '#F59E0B', '31-60': '#F97316', '60+': '#EF4444' };

export default function BlockedUnits() {
  const { token, period, refDate } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kpi/blocked-units`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const units = d.units || [];
  const byProject = d.by_project || [];
  const aging = d.aging || [];
  const byStatus = d.by_status || [];
  const byCustType = d.by_customer_type || [];
  const hasData = units.length > 0;

  const aiContext = hasData ? `Blocked units: ${d.total_units}, Total amount: ${fmt(d.total_amount)}, Avg days: ${d.avg_days_blocked}, Max: ${d.max_days_unit?.unit} in ${d.max_days_unit?.project} (${d.max_days_unit?.days} days). By project: ${byProject.slice(0,3).map(p => `${p.name}: ${p.count}`).join(', ')}.` : '';

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="blocked-units-page">
      <div><h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Blocked Units</h1></div>
      <div className="empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Blocked Units Data</p></div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden" data-testid="blocked-units-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Blocked Units Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Pipeline analysis — units blocked with money, aging, and conversion risk</p>
      </div>

      <AIPageInsight token={token} period={period} refDate={refDate} context={aiContext} pageTitle="Blocked Units" />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-4">
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1.5"><Lock className="h-4 w-4 text-amber-500" /><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Total Blocked</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{d.total_units || 0}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{byProject.length} projects</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1.5"><IndianRupee className="h-4 w-4 text-indigo-500" /><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Est. Pipeline</span></div>
          <p className="text-lg sm:text-xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_estimated_value)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Indicative value</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1.5"><IndianRupee className="h-4 w-4 text-blue-500" /><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Amt Collected</span></div>
          <p className="text-lg sm:text-xl font-bold text-blue-600" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_amount)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Booking money</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1.5"><Clock className="h-4 w-4 text-orange-500" /><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Avg Days</span></div>
          <p className="text-lg sm:text-xl font-bold text-orange-600" style={{ fontFamily: 'Manrope' }}>{d.avg_days_blocked || 0}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">days average</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1.5"><AlertTriangle className="h-4 w-4 text-red-500" /><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Longest Block</span></div>
          <p className="text-lg sm:text-xl font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>{d.max_days_unit?.days || 0} days</p>
          <p className="text-[10px] text-slate-400 mt-0.5 truncate">{d.max_days_unit?.unit}</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Aging Analysis */}
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Aging Analysis — Units & Value</CardTitle></CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={aging} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="range" tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 9, fill: '#94A3B8' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: '#94A3B8' }} tickFormatter={fmt} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={(v, name) => [name === 'est_value' ? fmt(v) : v, name === 'est_value' ? 'Est. Value' : 'Units']} />
                <Bar yAxisId="left" dataKey="units" name="Units" radius={[6, 6, 0, 0]} barSize={20}>
                  {aging.map((e, i) => <Cell key={i} fill={AGING_COLORS[i]} />)}
                </Bar>
                <Bar yAxisId="right" dataKey="est_value" name="Est. Value" fill="#6366F1" radius={[6, 6, 0, 0]} barSize={20} fillOpacity={0.3} stroke="#6366F1" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-5 gap-1 mt-3">
              {aging.map((a, i) => (
                <div key={i} className="text-center p-2 rounded-lg" style={{ background: `${AGING_COLORS[i]}10` }}>
                  <p className="text-xs font-bold" style={{ color: AGING_COLORS[i] }}>{a.units}</p>
                  <p className="text-[8px] text-slate-500">{a.range} days</p>
                  <p className="text-[9px] font-semibold text-slate-700">{fmt(a.est_value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Project */}
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Blocked by Project</CardTitle></CardHeader>
          <CardContent className="pt-3 sm:pt-4">
            <div className="space-y-3">
              {byProject.map((p, i) => {
                const maxCount = byProject[0]?.count || 1;
                const pct = Math.round((p.count / maxCount) * 100);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-slate-700 truncate flex-1 mr-2">{p.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-indigo-600 font-medium">{fmt(p.estimated_value)}</span>
                        <span className="text-[10px] text-slate-400">{p.count} units</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{ width: `${pct}%`, opacity: 1 - i * 0.12 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status + Customer Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>By Block Status</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {byStatus.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                  <span className="text-xs font-medium text-slate-700">{s.name}</span>
                  <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{s.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>By Customer Type</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {byCustType.map((ct, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                  <span className="text-xs font-medium text-slate-700">{ct.name}</span>
                  <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{ct.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unit-level table */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100"><CardTitle className="text-xs sm:text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>All Blocked Units (sorted by days)</CardTitle></CardHeader>
        <CardContent className="pt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Unit</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-600">Customer</th>
                  <th className="text-right py-2 px-2 font-semibold text-slate-600">SBA</th>
                  <th className="text-right py-2 px-2 font-semibold text-indigo-600">Est. Value</th>
                  <th className="text-right py-2 px-2 font-semibold text-blue-600">Collected</th>
                  <th className="text-right py-2 px-2 font-semibold text-orange-600">Days</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-slate-50/50' : ''}`}>
                    <td className="py-2 px-2 font-medium text-slate-800 max-w-[140px] truncate">{u.project}</td>
                    <td className="py-2 px-2 text-slate-700">{u.unit}</td>
                    <td className="py-2 px-2 text-slate-700 max-w-[130px] truncate">{u.customer}</td>
                    <td className="py-2 px-2 text-right text-slate-600">{u.sba ? u.sba.toLocaleString('en-IN') : '-'}</td>
                    <td className="py-2 px-2 text-right font-medium text-indigo-600">{u.estimated_value ? fmt(u.estimated_value) : '-'}</td>
                    <td className="py-2 px-2 text-right text-blue-600">{fmt(u.amount)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${(u.days || 0) > 30 ? 'bg-red-100 text-red-700' : (u.days || 0) > 15 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {u.days || 0}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
